import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getStatisticDefinitions, createStatisticDefinition, updateStatisticDefinition, deleteStatisticDefinition } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function StatisticsDefinitionsPage() {
  const { t, language } = useLanguage();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDef, setEditingDef] = useState(null);
  const [deletingDef, setDeletingDef] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    description: '',
    statistic_type: 'number',
    icon: '',
    display_order: 0,
    is_active: true,
    applies_to: 'global',
  });

  const fetchDefinitions = async () => {
    try {
      const response = await getStatisticDefinitions();
      setDefinitions(response.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const handleOpenDialog = (def = null) => {
    if (def) {
      setEditingDef(def);
      setFormData({
        code: def.code || '',
        name_ar: def.name_ar || '',
        name_en: def.name_en || '',
        description: def.description || '',
        statistic_type: def.statistic_type || 'number',
        icon: def.icon || '',
        display_order: def.display_order || 0,
        is_active: def.is_active !== false,
        applies_to: def.applies_to || 'global',
      });
    } else {
      setEditingDef(null);
      setFormData({
        code: '',
        name_ar: '',
        name_en: '',
        description: '',
        statistic_type: 'number',
        icon: '',
        display_order: 0,
        is_active: true,
        applies_to: 'global',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.name_ar.trim()) {
      toast.error(t('validation.required'));
      return;
    }
    
    setSaving(true);
    try {
      if (editingDef) {
        await updateStatisticDefinition(editingDef.id, formData);
        toast.success(t('success.updated'));
      } else {
        await createStatisticDefinition(formData);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchDefinitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDef) return;
    
    try {
      await deleteStatisticDefinition(deletingDef.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingDef(null);
      fetchDefinitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const filteredDefinitions = definitions.filter(def => 
    def.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    def.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    def.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="statistics-definitions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
            data-testid="stat-definitions-search"
          />
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#0A4C3A] hover:bg-[#073B2D]"
          data-testid="add-stat-definition-button"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('add_stat_definition')}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('stat_code')}</TableHead>
                <TableHead className="font-medium">{t('stat_name_ar')}</TableHead>
                <TableHead className="font-medium">{t('stat_type')}</TableHead>
                <TableHead className="font-medium">{t('applies_to')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDefinitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredDefinitions.map((def) => (
                  <TableRow key={def.id} data-testid={`stat-def-row-${def.id}`}>
                    <TableCell className="font-mono text-sm">{def.code}</TableCell>
                    <TableCell className="font-medium">{def.name_ar}</TableCell>
                    <TableCell>{t(def.statistic_type)}</TableCell>
                    <TableCell>{t(def.applies_to)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        def.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {def.is_active !== false ? t('active') : t('inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(def)}
                          data-testid={`edit-stat-def-${def.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingDef(def);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-stat-def-${def.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDef ? t('edit_stat_definition') : t('add_stat_definition')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('stat_code')} *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  dir="ltr"
                  data-testid="stat-def-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icon">{t('stat_icon')}</Label>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  dir="ltr"
                  data-testid="stat-def-icon"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_ar">{t('stat_name_ar')} *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                data-testid="stat-def-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">{t('stat_name_en')}</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                dir="ltr"
                data-testid="stat-def-name-en"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('stat_type')}</Label>
                <Select 
                  value={formData.statistic_type} 
                  onValueChange={(value) => setFormData({ ...formData, statistic_type: value })}
                >
                  <SelectTrigger data-testid="stat-def-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="number">{t('number')}</SelectItem>
                    <SelectItem value="percentage">{t('percentage')}</SelectItem>
                    <SelectItem value="currency">{t('currency')}</SelectItem>
                    <SelectItem value="text">{t('text')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('applies_to')}</Label>
                <Select 
                  value={formData.applies_to} 
                  onValueChange={(value) => setFormData({ ...formData, applies_to: value })}
                >
                  <SelectTrigger data-testid="stat-def-applies-to-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">{t('global')}</SelectItem>
                    <SelectItem value="region">{t('region')}</SelectItem>
                    <SelectItem value="city">{t('city')}</SelectItem>
                    <SelectItem value="category">{t('category')}</SelectItem>
                    <SelectItem value="projects">{t('projects')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('stat_description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="stat-def-description"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">{t('active')}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="stat-def-is-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-stat-def-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingDef?.name_ar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-stat-def"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
